import { useCallback, useEffect, useState } from 'react';
import {
  organisation,
  type City,
  type Country,
  type Department,
  type District,
  type LocationType,
  type Operation,
  type Region,
} from '../api/organisation';
import { tn } from '../i18n';
import { useTranslate } from '../i18n/react';
import { Badge } from '../ui/Badge';
import { Field, Select } from '../ui/Field';
import { ActiveChip, CountChip, DefinitionTable } from './DefinitionTable';

/**
 * The organisation definition screens, all seven of them.
 *
 * Together in one file because they are one idea seen seven times — a list, four fields and
 * a delete that counts what hangs off the row first. The location card is the one that is
 * genuinely different, and it lives on its own.
 */

/** Loads a list and gives back a way to load it again. */
function useRows<T>(load: () => Promise<T[]>, deps: unknown[]) {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await load());
    } finally {
      setLoading(false);
    }
  }, deps);

  useEffect(() => {
    void reload();
  }, [reload]);

  return { rows, loading, reload };
}

/** The parent picker that sits in a child screen's bar — a city needs its country first. */
function Picker({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { id: string; name: string }[];
  onChange: (value: string) => void;
}) {
  const t = useTranslate();
  return (
    <Field label={label}>
      {({ id }) => (
        <Select id={id} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('common.all')}</option>
          {options.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </Select>
      )}
    </Field>
  );
}

/**
 * Every readable field of a row, for the search box: the name, the code, the parent it sits
 * under. Ids are left out so a search for "01a1" does not match everything.
 */
function searchText(row: object): string {
  return Object.entries(row)
    .filter(([key, value]) => typeof value === 'string' && !/id$/i.test(key))
    .map(([, value]) => value as string)
    .join(' ');
}

// ---------------------------------------------------------------- location types

interface TypeDraft {
  code: string;
  name: string;
  description: string;
  isActive: boolean;
}

export function LocationTypes() {
  const t = useTranslate();
  const { rows, loading, reload } = useRows(() => organisation.locationTypes.list(), []);

  return (
    <DefinitionTable<LocationType, TypeDraft>
      title={t('locationTypes.title')}
      subtitle={t('locationTypes.subtitle')}
      rows={rows}
      searchable={searchText}
      loading={loading}
      blank={{ code: '', name: '', description: '', isActive: true }}
      toDraft={(r) => ({
        code: r.code,
        name: r.name,
        description: r.description ?? '',
        isActive: r.isActive,
      })}
      fields={[
        { key: 'code', label: t('common.code'), tight: true, placeholder: 'AVM' },
        { key: 'name', label: t('common.name') },
        { key: 'description', label: t('common.description') },
        { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
      ]}
      columns={[
        {
          label: t('locationTypes.locations'),
          render: (r) => <CountChip count={r.locationCount} label={t('locationTypes.locations')} />,
        },
      ]}
      cell={(row, key) =>
        key === 'isActive' ? (
          <ActiveChip active={row.isActive} />
        ) : key === 'description' ? (
          (row.description ?? <span className="nv-hint">{t('common.none')}</span>)
        ) : key === 'code' ? (
          <span className="nv-code">{row.code}</span>
        ) : (
          row.name
        )
      }
      save={async (d, id) => {
        const body = {
          code: d.code.trim(),
          name: d.name.trim(),
          description: d.description.trim() || null,
          isActive: d.isActive,
        };
        if (id) await organisation.locationTypes.update(id, body);
        else await organisation.locationTypes.create(body);
        await reload();
      }}
      remove={async (row) => {
        await organisation.locationTypes.remove(row.id);
        await reload();
      }}
      describe={(row) => ({
        name: row.name,
        warning: row.locationCount > 0 ? tn('locationTypes.inUseWarning', row.locationCount) : undefined,
      })}
      empty={t('locationTypes.empty')}
    />
  );
}

// ---------------------------------------------------------------- departments

/**
 * Departments carry one field the other definition tables do not: whether the people seated
 * here work at head office. It decides which list they appear in when an auditor hands a
 * finding to somebody, so it is edited where the department is edited and nowhere else.
 */
interface DepartmentDraft extends TypeDraft {
  isHeadOffice: boolean;
}

export function Departments() {
  const t = useTranslate();
  const { rows, loading, reload } = useRows(() => organisation.departments.list(), []);

  return (
    <DefinitionTable<Department, DepartmentDraft>
      title={t('departments.title')}
      subtitle={t('departments.subtitle')}
      rows={rows}
      searchable={searchText}
      loading={loading}
      blank={{ code: '', name: '', description: '', isActive: true, isHeadOffice: false }}
      toDraft={(r) => ({
        code: r.code,
        name: r.name,
        description: r.description ?? '',
        isActive: r.isActive,
        isHeadOffice: r.isHeadOffice,
      })}
      fields={[
        { key: 'code', label: t('common.code'), tight: true, placeholder: 'VM' },
        { key: 'name', label: t('common.name') },
        { key: 'description', label: t('common.description') },
        { key: 'isHeadOffice', label: t('departments.headOffice'), kind: 'toggle', tight: true },
        { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
      ]}
      cell={(row, key) =>
        key === 'isActive' ? (
          <ActiveChip active={row.isActive} />
        ) : key === 'isHeadOffice' ? (
          // Square, because this is what the department *is* rather than how it behaves.
          <Badge tone={row.isHeadOffice ? 'comment' : 'neutral'} shape="square">
            {t(row.isHeadOffice ? 'departments.headOfficeShort' : 'departments.storeShort')}
          </Badge>
        ) : key === 'description' ? (
          (row.description ?? <span className="nv-hint">{t('common.none')}</span>)
        ) : key === 'code' ? (
          <span className="nv-code">{row.code}</span>
        ) : (
          row.name
        )
      }
      save={async (d, id) => {
        const body = {
          code: d.code.trim(),
          name: d.name.trim(),
          description: d.description.trim() || null,
          isActive: d.isActive,
          isHeadOffice: d.isHeadOffice,
        };
        if (id) await organisation.departments.update(id, body);
        else await organisation.departments.create(body);
        await reload();
      }}
      remove={async (row) => {
        await organisation.departments.remove(row.id);
        await reload();
      }}
      describe={(row) => ({ name: row.name })}
      empty={t('departments.empty')}
    />
  );
}

// ---------------------------------------------------------------- operations

interface CodeDraft {
  code: string;
  name: string;
  isActive: boolean;
}

export function Operations() {
  const t = useTranslate();
  const { rows, loading, reload } = useRows(() => organisation.operations.list(), []);

  return (
    <DefinitionTable<Operation, CodeDraft>
      title={t('operations.title')}
      subtitle={t('operations.subtitle')}
      rows={rows}
      searchable={searchText}
      loading={loading}
      blank={{ code: '', name: '', isActive: true }}
      toDraft={(r) => ({ code: r.code, name: r.name, isActive: r.isActive })}
      fields={[
        { key: 'code', label: t('common.code'), tight: true, placeholder: 'YRGC01' },
        { key: 'name', label: t('common.name') },
        { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
      ]}
      columns={[
        {
          label: t('operations.regions'),
          render: (r) => <CountChip count={r.regionCount} label={t('operations.regions')} />,
        },
      ]}
      cell={(row, key) =>
        key === 'isActive' ? (
          <ActiveChip active={row.isActive} />
        ) : key === 'code' ? (
          <span className="nv-code">{row.code}</span>
        ) : (
          row.name
        )
      }
      save={async (d, id) => {
        const body = { code: d.code.trim(), name: d.name.trim(), isActive: d.isActive };
        if (id) await organisation.operations.update(id, body);
        else await organisation.operations.create(body);
        await reload();
      }}
      remove={async (row) => {
        await organisation.operations.remove(row.id);
        await reload();
      }}
      describe={(row) => ({
        name: row.name,
        warning: row.regionCount > 0 ? tn('operations.inUseWarning', row.regionCount) : undefined,
      })}
      empty={t('operations.empty')}
    />
  );
}

// ---------------------------------------------------------------- regions

interface RegionDraft extends CodeDraft {
  operationId: string;
}

export function Regions() {
  const t = useTranslate();
  const [operationId, setOperationId] = useState('');
  const { rows: operations, loading: operationsLoading } = useRows(() => organisation.operations.list(), []);
  const { rows, loading, reload } = useRows(
    () => organisation.regions.list({ operationId: operationId || undefined }),
    [operationId],
  );

  return (
    <DefinitionTable<Region, RegionDraft>
      title={t('regions.title')}
      subtitle={t('regions.subtitle')}
      rows={rows}
      searchable={searchText}
      loading={loading}
      blank={{ operationId: operationId || operations[0]?.id || '', code: '', name: '', isActive: true }}
      toDraft={(r) => ({
        operationId: r.operationId,
        code: r.code,
        name: r.name,
        isActive: r.isActive,
      })}
      fields={[
        {
          key: 'operationId',
          label: t('regions.operation'),
          kind: 'select',
          options: operations.map((o) => ({ value: o.id, label: o.name })),
        },
        { key: 'code', label: t('common.code'), tight: true, placeholder: 'B1' },
        { key: 'name', label: t('common.name') },
        { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
      ]}
      columns={[
        {
          label: t('regions.locations'),
          render: (r) => <CountChip count={r.locationCount} label={t('regions.locations')} />,
        },
      ]}
      cell={(row, key) =>
        key === 'isActive' ? (
          <ActiveChip active={row.isActive} />
        ) : key === 'operationId' ? (
          row.operationName
        ) : key === 'code' ? (
          <span className="nv-code">{row.code}</span>
        ) : (
          row.name
        )
      }
      save={async (d, id) => {
        const body = {
          operationId: d.operationId,
          code: d.code.trim(),
          name: d.name.trim(),
          isActive: d.isActive,
        };
        if (id) await organisation.regions.update(id, body);
        else await organisation.regions.create(body);
        await reload();
      }}
      remove={async (row) => {
        await organisation.regions.remove(row.id);
        await reload();
      }}
      describe={(row) => ({
        name: row.name,
        warning: row.locationCount > 0 ? tn('regions.inUseWarning', row.locationCount) : undefined,
      })}
      empty={t('regions.empty')}
      // A region has to belong to an operation, so the operation list is what has to exist
      // first. Saying so is kinder than a select with nothing in it.
      blocked={!operationsLoading && operations.length === 0 ? t('regions.needOperation') : undefined}
    >
      <Picker
        label={t('regions.operation')}
        value={operationId}
        options={operations}
        onChange={setOperationId}
      />
    </DefinitionTable>
  );
}

// ---------------------------------------------------------------- countries

export function Countries() {
  const t = useTranslate();
  const { rows, loading, reload } = useRows(() => organisation.countries.list(), []);

  return (
    <DefinitionTable<Country, CodeDraft>
      title={t('countries.title')}
      subtitle={t('countries.subtitle')}
      rows={rows}
      searchable={searchText}
      loading={loading}
      blank={{ code: '', name: '', isActive: true }}
      toDraft={(r) => ({ code: r.code, name: r.name, isActive: r.isActive })}
      fields={[
        { key: 'code', label: t('common.code'), tight: true, placeholder: 'TR' },
        { key: 'name', label: t('common.name') },
        { key: 'isActive', label: t('common.status'), kind: 'toggle', tight: true },
      ]}
      columns={[
        {
          label: t('countries.cities'),
          render: (r) => <CountChip count={r.cityCount} label={t('countries.cities')} />,
        },
        {
          label: t('common.locations'),
          render: (r) => <CountChip count={r.locationCount} label={t('common.locations')} />,
        },
      ]}
      cell={(row, key) =>
        key === 'isActive' ? (
          <ActiveChip active={row.isActive} />
        ) : key === 'code' ? (
          <span className="nv-code">{row.code}</span>
        ) : (
          row.name
        )
      }
      save={async (d, id) => {
        const body = { code: d.code.trim(), name: d.name.trim(), isActive: d.isActive };
        if (id) await organisation.countries.update(id, body);
        else await organisation.countries.create(body);
        await reload();
      }}
      remove={async (row) => {
        await organisation.countries.remove(row.id);
        await reload();
      }}
      describe={(row) => ({
        name: row.name,
        warning:
          row.locationCount > 0
            ? tn('countries.inUseWarning', row.locationCount)
            : row.cityCount > 0
              ? tn('countries.hasCitiesWarning', row.cityCount)
              : undefined,
      })}
      empty={t('countries.empty')}
    />
  );
}

// ---------------------------------------------------------------- cities

interface CityDraft {
  countryId: string;
  name: string;
}

export function Cities() {
  const t = useTranslate();
  const [countryId, setCountryId] = useState('');
  const { rows: countries, loading: countriesLoading } = useRows(() => organisation.countries.list(), []);
  const { rows, loading, reload } = useRows(
    () => organisation.cities.list({ countryId: countryId || undefined }),
    [countryId],
  );

  return (
    <DefinitionTable<City, CityDraft>
      title={t('cities.title')}
      subtitle={t('cities.subtitle')}
      rows={rows}
      searchable={searchText}
      loading={loading}
      blank={{ countryId: countryId || countries[0]?.id || '', name: '' }}
      toDraft={(r) => ({ countryId: r.countryId, name: r.name })}
      fields={[
        {
          key: 'countryId',
          label: t('cities.country'),
          kind: 'select',
          options: countries.map((c) => ({ value: c.id, label: c.name })),
        },
        { key: 'name', label: t('common.name') },
      ]}
      columns={[
        {
          label: t('cities.districts'),
          render: (r) => <CountChip count={r.districtCount} label={t('cities.districts')} />,
        },
        {
          label: t('common.locations'),
          render: (r) => <CountChip count={r.locationCount} label={t('common.locations')} />,
        },
      ]}
      cell={(row, key) => (key === 'countryId' ? row.countryName : row.name)}
      save={async (d, id) => {
        const body = { countryId: d.countryId, name: d.name.trim() };
        if (id) await organisation.cities.update(id, body);
        else await organisation.cities.create(body);
        await reload();
      }}
      remove={async (row) => {
        await organisation.cities.remove(row.id);
        await reload();
      }}
      describe={(row) => ({
        name: row.name,
        warning:
          row.locationCount > 0
            ? tn('cities.inUseWarning', row.locationCount)
            : row.districtCount > 0
              ? tn('cities.hasDistrictsWarning', row.districtCount)
              : undefined,
      })}
      empty={t('cities.empty')}
      blocked={!countriesLoading && countries.length === 0 ? t('cities.needCountry') : undefined}
    >
      <Picker
        label={t('cities.country')}
        value={countryId}
        options={countries}
        onChange={setCountryId}
      />
    </DefinitionTable>
  );
}

// ---------------------------------------------------------------- districts

interface DistrictDraft {
  cityId: string;
  name: string;
}

export function Districts() {
  const t = useTranslate();
  const [cityId, setCityId] = useState('');
  const { rows: cities, loading: citiesLoading } = useRows(() => organisation.cities.list(), []);
  const { rows, loading, reload } = useRows(
    () => organisation.districts.list({ cityId: cityId || undefined }),
    [cityId],
  );

  return (
    <DefinitionTable<District, DistrictDraft>
      title={t('districts.title')}
      subtitle={t('districts.subtitle')}
      rows={rows}
      searchable={searchText}
      loading={loading}
      blank={{ cityId: cityId || cities[0]?.id || '', name: '' }}
      toDraft={(r) => ({ cityId: r.cityId, name: r.name })}
      fields={[
        {
          key: 'cityId',
          label: t('districts.city'),
          kind: 'select',
          options: cities.map((c) => ({ value: c.id, label: `${c.name} · ${c.countryName}` })),
        },
        { key: 'name', label: t('common.name') },
      ]}
      columns={[
        {
          label: t('common.locations'),
          render: (r) => <CountChip count={r.locationCount} label={t('common.locations')} />,
        },
      ]}
      cell={(row, key) => (key === 'cityId' ? row.cityName : row.name)}
      save={async (d, id) => {
        const body = { cityId: d.cityId, name: d.name.trim() };
        if (id) await organisation.districts.update(id, body);
        else await organisation.districts.create(body);
        await reload();
      }}
      remove={async (row) => {
        await organisation.districts.remove(row.id);
        await reload();
      }}
      describe={(row) => ({
        name: row.name,
        warning: row.locationCount > 0 ? tn('districts.inUseWarning', row.locationCount) : undefined,
      })}
      empty={t('districts.empty')}
      blocked={!citiesLoading && cities.length === 0 ? t('districts.needCity') : undefined}
    >
      <Picker label={t('districts.city')} value={cityId} options={cities} onChange={setCityId} />
    </DefinitionTable>
  );
}
